import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BalanceIcon from "@mui/icons-material/Balance";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import PaidIcon from "@mui/icons-material/Paid";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { buildApiUrl } from "./apiConfig";
import { emitInvestitiiProductDeleted } from "./investitiiEvents";

const INVESTITII_API = buildApiUrl("/api/investitii");
const WOO_CATALOG_SYNC_API = buildApiUrl("/sync-products");

const moneyFormatter = new Intl.NumberFormat("ro-RO", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const ronFormatter = new Intl.NumberFormat("ro-RO", {
  style: "currency",
  currency: "RON",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("ro-RO", {
  maximumFractionDigits: 2,
});

const fxPreciseFormatter = new Intl.NumberFormat("ro-RO", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const today = () => new Date().toISOString().slice(0, 10);

const initialInvestor = {
  name: "",
  phone: "",
  email: "",
  iban: "",
  contractRef: "",
  notes: "",
};

const initialPool = {
  investorId: "",
  name: "",
  initialCapital: "",
  monthlyInterest: "",
  dueDay: 4,
  receivedDate: today(),
  currency: "EUR",
  notes: "",
};

const initialCapital = {
  poolId: "",
  direction: "in",
  amount: "",
  date: today(),
  notes: "",
};

const initialProduct = {
  poolId: "",
  contractId: "",
  jiraIssueId: "",
  jiraJournalNumber: "",
  sku: "",
  brand: "",
  model: "",
  purchasePrice: "",
  tipAchizitie: "",
  isConsignmentPurchase: false,
  purchasePriceMissing: false,
  extraCosts: "",
  listingPrice: "",
  purchaseDate: today(),
  notes: "",
};

const initialProductEdit = {
  sku: "",
  brand: "",
  model: "",
  purchasePriceRon: "",
  isConsignmentPurchase: false,
  purchasePriceMissing: false,
  extraCostsRon: "",
  listingPriceRon: "",
  purchaseDate: today(),
  status: "in_stock",
  notes: "",
};

const initialSale = {
  productId: "",
  salePrice: "",
  commission: "",
  receivedAmount: "",
  saleDate: today(),
  notes: "",
};

const initialInterest = {
  poolId: "",
  action: "due",
  amount: "",
  date: today(),
  notes: "",
};

function formatMoney(value) {
  return moneyFormatter.format(Number(value || 0));
}

function formatRon(value) {
  return ronFormatter.format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "-";
  return String(value).slice(0, 10).split("-").reverse().join(".");
}

function formatWeekday(value) {
  if (!value) return "-";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return formatDate(value);
  return new Intl.DateTimeFormat("ro-RO", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
  }).format(date);
}

function formatStockDays(value) {
  if (value === null || value === undefined || value === "") return "-";
  const days = Number(value || 0);
  return `${numberFormatter.format(days)} ${days === 1 ? "zi" : "zile"}`;
}

function nonNegativeNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.max(0, numericValue) : 0;
}

function optionalNonNegativeNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.max(0, numericValue) : null;
}

function getInterestCoverage({
  interestToCover,
  remainingInterest,
  coveredInterest,
  realizedProfit,
}) {
  const statedRemaining = optionalNonNegativeNumber(remainingInterest);
  const statedCovered = optionalNonNegativeNumber(coveredInterest);
  const realized = nonNegativeNumber(realizedProfit);
  const targetInterest = Math.max(
    nonNegativeNumber(interestToCover),
    (statedRemaining || 0) + (statedCovered || 0)
  );
  const covered = Math.min(
    targetInterest,
    statedCovered ??
      (statedRemaining === null
        ? realized
        : Math.max(0, targetInterest - statedRemaining))
  );
  const outstanding = Math.min(
    targetInterest,
    statedRemaining ?? Math.max(0, targetInterest - covered)
  );

  return {
    targetInterest,
    coveredInterest: covered,
    outstandingInterest: outstanding,
  };
}

function normalizeSku(value) {
  return String(value || "").trim().toUpperCase();
}

function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function editValue(value) {
  return value === null || value === undefined ? "" : String(value);
}

function isConsignmentPurchase(product) {
  return (
    product?.isConsignmentPurchase === true ||
    String(product?.tipAchizitie || "").trim().toUpperCase().startsWith("LP002")
  );
}

function buildProductEditForm(product) {
  return {
    sku: editValue(product?.sku),
    brand: editValue(product?.brand),
    model: editValue(product?.model),
    purchasePriceRon: product?.purchasePriceMissing
      ? ""
      : editValue(
          product?.purchasePriceCorrectionPending
            ? product.consignmentPurchasePriceRon
            : product?.purchasePriceRon
        ),
    isConsignmentPurchase: isConsignmentPurchase(product),
    purchasePriceMissing: Boolean(product?.purchasePriceMissing),
    extraCostsRon: editValue(product?.extraCostsRon),
    listingPriceRon: editValue(product?.listingPriceRon),
    purchaseDate: product?.purchaseDate || today(),
    status: product?.status || "in_stock",
    notes: editValue(product?.notes),
  };
}

function statusLabel(status) {
  const labels = {
    active: "activ",
    closed: "închis",
    paused: "pauză",
    in_stock: "în stoc",
    reserved: "rezervat",
    sold: "vândut",
    returned: "returnat",
  };
  return labels[status] || status || "-";
}

function priceWarningLabel(warning) {
  if (!warning) return "";
  if (warning.type === "price_mismatch") {
    return `Jira ${formatRon(warning.jiraSalePriceRon)} / Woo ${formatRon(
      warning.wooPriceRon
    )}`;
  }
  if (warning.type === "missing_woo_product") return "SKU lipsă în Woo";
  if (warning.type === "missing_jira_price") return "Preț Jira lipsă";
  if (warning.type === "missing_woo_price") return "Preț Woo lipsă";
  return "Verifică Jira/Woo";
}

function wooOrdersSyncSummary(result = {}) {
  const synced = Array.isArray(result.synced) ? result.synced : [];
  const autoRestocked = Array.isArray(result.autoRestocked)
    ? result.autoRestocked
    : [];
  const pendingReviews = Array.isArray(result.pendingReviews)
    ? result.pendingReviews
    : [];
  const refundSkipped = Array.isArray(result.refundSkipped)
    ? result.refundSkipped
    : [];
  const syncedCount = Number(result.syncedCount ?? synced.length);
  const autoRestockedCount = Number(
    result.autoRestockedCount ?? autoRestocked.length
  );
  const pendingReviewCount = Number(
    result.pendingReviewCount ?? pendingReviews.length
  );
  const wooOrdersCount = Number(result.wooRefresh?.updatedCount || 0);
  const wooSince = result.wooRefresh?.after;
  const soldSkus = synced
    .map((item) => item.sku)
    .filter(Boolean)
    .slice(0, 6)
    .join(", ");
  const restockedSkus = autoRestocked
    .map((item) => item.sku)
    .filter(Boolean)
    .slice(0, 6)
    .join(", ");
  const pendingSkus = pendingReviews
    .map((item) => item.sku)
    .filter(Boolean)
    .slice(0, 6)
    .join(", ");
  const warning = result.warning ? ` ${result.warning}` : "";

  return {
    type:
      pendingReviewCount > 0 || refundSkipped.length > 0 || result.warning
        ? "warning"
        : syncedCount > 0 || autoRestockedCount > 0
        ? "success"
        : "info",
    text:
      `Verificare comenzi completă: ${syncedCount} vânzări noi, ` +
      `${autoRestockedCount} produse repuse automat în stoc și ` +
      `${pendingReviewCount} retururi de confirmat manual. ` +
      `Am analizat ${wooOrdersCount} comenzi Woo${
        wooSince ? ` din ${wooSince}` : ""
      }.` +
      (soldSkus ? ` Vândute: ${soldSkus}.` : "") +
      (restockedSkus ? ` Reintrate în stoc: ${restockedSkus}.` : "") +
      (pendingSkus ? ` De confirmat: ${pendingSkus}.` : "") +
      (refundSkipped.length
        ? refundSkipped.length === 1
          ? " Un retur nu a putut fi reconciliat automat."
          : ` ${refundSkipped.length} retururi nu au putut fi reconciliate automat.`
        : "") +
      warning,
  };
}

function typeLabel(type) {
  const labels = {
    capital_in: "capital primit",
    capital_out: "capital retras",
    product_purchase: "cumpărare produs",
    product_sale: "vânzare produs",
    interest_due: "dobândă datorată",
    interest_paid: "dobândă plătită",
    expense: "cost extern",
  };
  return labels[type] || type || "-";
}

const dashboardTones = {
  blue: {
    accent: "#2563eb",
    soft: "#eff6ff",
    border: "#bfdbfe",
    text: "#1d4ed8",
  },
  teal: {
    accent: "#0f766e",
    soft: "#f0fdfa",
    border: "#99f6e4",
    text: "#0f766e",
  },
  amber: {
    accent: "#b45309",
    soft: "#fffbeb",
    border: "#fde68a",
    text: "#92400e",
  },
  coral: {
    accent: "#c2410c",
    soft: "#fff7ed",
    border: "#fed7aa",
    text: "#c2410c",
  },
};

function DashboardSectionHeader({ eyebrow, title, helper }) {
  return (
    <Box component="header" sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "#667085",
          fontWeight: 900,
          letterSpacing: 0,
          textTransform: "uppercase",
        }}
      >
        {eyebrow}
      </Typography>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 0.35, md: 1.5 }}
        alignItems={{ xs: "flex-start", md: "baseline" }}
      >
        <Typography component="h2" sx={{ color: "#101828", fontSize: 20, fontWeight: 900 }}>
          {title}
        </Typography>
        {helper ? (
          <Typography variant="body2" sx={{ color: "#667085" }}>
            {helper}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

function DashboardMetric({ label, value, helper, icon, tone = "blue" }) {
  const palette = dashboardTones[tone] || dashboardTones.blue;

  return (
    <Paper
      component="article"
      elevation={0}
      sx={{
        position: "relative",
        height: "100%",
        minHeight: 146,
        overflow: "hidden",
        border: `1px solid ${palette.border}`,
        borderRadius: "8px",
        p: 2,
        background: "#fff",
        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.045)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: palette.accent,
        },
      }}
    >
      <Stack spacing={1.25} sx={{ height: "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="caption" sx={{ color: "#475467", fontWeight: 800 }}>
            {label}
          </Typography>
          <Box
            sx={{
              width: 34,
              height: 34,
              display: "grid",
              placeItems: "center",
              borderRadius: "8px",
              color: palette.text,
              background: palette.soft,
              border: `1px solid ${palette.border}`,
              flex: "0 0 auto",
            }}
          >
            {icon}
          </Box>
        </Stack>
        <Typography sx={{ color: "#101828", fontSize: 27, fontWeight: 900, lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: "#667085", mt: "auto", lineHeight: 1.4 }}>
          {helper}
        </Typography>
      </Stack>
    </Paper>
  );
}

function PoolDataPoint({ label, value, color = "#101828" }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ display: "block", color: "#667085", fontSize: 11, fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Typography noWrap sx={{ color, fontSize: 14, fontWeight: 850 }}>
        {value}
      </Typography>
    </Box>
  );
}

function MonthlyProfitBalance({
  interestToCover,
  remainingInterest,
  interestCovered,
  netProfit,
  realizedProfit,
}) {
  const monthlyRealizedProfit = nonNegativeNumber(realizedProfit);
  const {
    targetInterest,
    coveredInterest,
    outstandingInterest,
  } = getInterestCoverage({
    interestToCover,
    remainingInterest,
    coveredInterest: interestCovered,
    realizedProfit: monthlyRealizedProfit,
  });
  const reportedNetProfit = optionalNonNegativeNumber(netProfit);
  const monthlyNetProfit =
    reportedNetProfit ?? Math.max(0, monthlyRealizedProfit - targetInterest);
  const debtWidth = targetInterest
    ? Math.min(100, (outstandingInterest / targetInterest) * 100)
    : 0;
  const profitWidth = targetInterest
    ? Math.min(100, (monthlyNetProfit / targetInterest) * 100)
    : monthlyNetProfit
    ? 100
    : 0;
  const stateLabel = outstandingInterest
    ? "Dobândă de acoperit"
    : monthlyNetProfit
    ? "Profit peste dobândă"
    : targetInterest
    ? "Dobândă acoperită"
    : "Fără dobândă lunară";
  const stateColor = outstandingInterest
    ? "#b42318"
    : monthlyNetProfit
    ? "#067647"
    : "#475467";
  const stateBackground = outstandingInterest
    ? "#fef3f2"
    : monthlyNetProfit
    ? "#ecfdf3"
    : "#f2f4f7";
  const stateBorder = outstandingInterest
    ? "#fecdca"
    : monthlyNetProfit
    ? "#abefc6"
    : "#d0d5dd";

  return (
    <Paper
      component="article"
      elevation={0}
      sx={{
        border: "1px solid #d0d5dd",
        borderRadius: "8px",
        p: { xs: 2, md: 2.5 },
        background: "#fff",
        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
                borderRadius: "8px",
                color: "#344054",
                background: "#f8fafc",
                border: "1px solid #d0d5dd",
              }}
            >
              <BalanceIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ color: "#101828", fontWeight: 900 }}>
                Balanța lunii
              </Typography>
              <Typography variant="caption" sx={{ color: "#667085" }}>
                Dobânda lunară scade cu profitul realizat din vânzări
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={stateLabel}
            sx={{
              height: 26,
              borderRadius: "6px",
              color: stateColor,
              background: stateBackground,
              border: `1px solid ${stateBorder}`,
              fontWeight: 850,
            }}
          />
        </Stack>

        <Box
          aria-label={`Balanță lunară: ${formatMoney(
            targetInterest
          )} obligație, ${formatMoney(
            coveredInterest
          )} acoperită din vânzări, ${formatMoney(
            outstandingInterest
          )} rămasă și ${formatMoney(monthlyNetProfit)} profit net Luxura.`}
        >
          <Stack
            direction="row"
            spacing={{ xs: 0.5, md: 1 }}
            alignItems="end"
            sx={{ mb: 0.9 }}
          >
            <Box sx={{ flex: "1 1 0", minWidth: 0, textAlign: "right" }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#b42318",
                  fontSize: { xs: 10, md: 12 },
                  fontWeight: 850,
                  lineHeight: 1.25,
                }}
              >
                Rămas din dobândă
              </Typography>
              <Typography
                noWrap
                sx={{ color: "#b42318", fontSize: { xs: 15, md: 18 }, fontWeight: 900 }}
              >
                {formatMoney(outstandingInterest)}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                width: { xs: 18, md: 28 },
                color: "#667085",
                fontWeight: 850,
                textAlign: "center",
              }}
            >
              0
            </Typography>
            <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#067647",
                  fontSize: { xs: 10, md: 12 },
                  fontWeight: 850,
                  lineHeight: 1.25,
                }}
              >
                Profit peste dobândă
              </Typography>
              <Typography
                noWrap
                sx={{ color: "#067647", fontSize: { xs: 15, md: 18 }, fontWeight: 900 }}
              >
                {formatMoney(monthlyNetProfit)}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) 4px minmax(0, 1fr)",
              height: 30,
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: "8px 0 0 8px",
                background: "#f8fafc",
                border: "1px solid #e4e7ec",
                borderRight: 0,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: `${debtWidth}%`,
                  background: "#dc2626",
                  transition: "width 360ms ease",
                }}
              />
            </Box>
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                background: "#101828",
                boxShadow: "0 0 0 2px #fff",
              }}
            />
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: "0 8px 8px 0",
                background: "#f8fafc",
                border: "1px solid #e4e7ec",
                borderLeft: 0,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: `${profitWidth}%`,
                  background: "#059669",
                  transition: "width 360ms ease",
                }}
              />
            </Box>
          </Box>
        </Box>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 0.5, md: 2.5 }}
          sx={{ color: "#667085" }}
        >
          <Typography variant="caption">
            Obligație lunară: <Box component="span" sx={{ color: "#101828", fontWeight: 850 }}>{formatMoney(targetInterest)}</Box>
          </Typography>
          <Typography variant="caption">
            Acoperită din vânzări: <Box component="span" sx={{ color: "#101828", fontWeight: 850 }}>{formatMoney(coveredInterest)}</Box>
            {targetInterest ? ` din ${formatMoney(targetInterest)}` : ""}
          </Typography>
          <Typography variant="caption">
            Profit realizat în lună: <Box component="span" sx={{ color: "#101828", fontWeight: 850 }}>{formatMoney(monthlyRealizedProfit)}</Box>
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ProfitSummaryPanel({
  currentNet,
  totalNet,
  currentProfit,
  totalProfit,
  unrealizedProfit,
  hasInterestBalance,
}) {
  const palette = hasInterestBalance ? dashboardTones.amber : dashboardTones.teal;

  return (
    <Paper
      component="article"
      elevation={0}
      sx={{
        height: "100%",
        minHeight: 226,
        border: `1px solid ${palette.border}`,
        borderRadius: "8px",
        p: { xs: 2, md: 2.5 },
        background: palette.soft,
      }}
    >
      <Stack spacing={2.25} sx={{ height: "100%" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                display: "grid",
                placeItems: "center",
                borderRadius: "8px",
                color: palette.text,
                background: "#fff",
                border: `1px solid ${palette.border}`,
              }}
            >
              <TrendingUpIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ color: "#101828", fontWeight: 900 }}>
                Profit net Luxura
              </Typography>
              <Typography variant="caption" sx={{ color: "#667085" }}>
                După acoperirea dobânzii automate
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={hasInterestBalance ? "Dobândă de acoperit" : "Dobândă acoperită"}
            sx={{
              height: 26,
              borderRadius: "6px",
              color: palette.text,
              background: "#fff",
              border: `1px solid ${palette.border}`,
              fontWeight: 850,
            }}
          />
        </Stack>

        <Divider sx={{ borderColor: palette.border }} />

        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Typography variant="caption" sx={{ color: "#475467", fontWeight: 800 }}>
              Luna curentă
            </Typography>
            <Typography
              sx={{ color: palette.text, fontSize: { xs: 30, md: 36 }, fontWeight: 950, lineHeight: 1.1 }}
            >
              {currentNet}
            </Typography>
            <Typography variant="caption" sx={{ color: "#667085" }}>
              Profit realizat în lună: {currentProfit}
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: "100%",
                pl: { xs: 0, md: 2.5 },
                pt: { xs: 1.5, md: 0 },
                borderTop: { xs: `1px solid ${palette.border}`, md: "none" },
                borderLeft: { xs: "none", md: `1px solid ${palette.border}` },
              }}
            >
              <PoolDataPoint label="Profit net total" value={totalNet} color={palette.text} />
              <Box sx={{ mt: 1.25 }}>
                <PoolDataPoint label="Profit realizat total" value={totalProfit} />
              </Box>
              <Box sx={{ mt: 1.25 }}>
                <PoolDataPoint label="Profit nerealizat în stoc" value={unrealizedProfit} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
}

function InterestSummaryPanel({
  interestToCover,
  balance,
  coveredInterest,
  realizedProfit,
  nextDueDate,
}) {
  const coverage = getInterestCoverage({
    interestToCover,
    remainingInterest: balance,
    coveredInterest,
    realizedProfit,
  });
  const hasBalance = coverage.outstandingInterest > 0;
  const palette = hasBalance ? dashboardTones.amber : dashboardTones.blue;

  return (
    <Paper
      component="article"
      elevation={0}
      sx={{
        height: "100%",
        minHeight: 226,
        border: `1px solid ${palette.border}`,
        borderRadius: "8px",
        p: { xs: 2, md: 2.5 },
        background: "#fff",
      }}
    >
      <Stack spacing={1.75} sx={{ height: "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" sx={{ color: "#475467", fontWeight: 800 }}>
              Dobândă rămasă de acoperit
            </Typography>
            <Typography sx={{ color: "#101828", mt: 0.6, fontSize: 28, fontWeight: 900, lineHeight: 1.1 }}>
              {formatMoney(coverage.outstandingInterest)}
            </Typography>
            <Typography variant="caption" sx={{ color: "#667085" }}>
              Din obligația lunii: {formatMoney(coverage.targetInterest)}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 38,
              height: 38,
              display: "grid",
              placeItems: "center",
              borderRadius: "8px",
              color: palette.text,
              background: palette.soft,
              border: `1px solid ${palette.border}`,
            }}
          >
            <EventRepeatIcon fontSize="small" />
          </Box>
        </Stack>

        <Divider sx={{ borderColor: "#eaecf0" }} />

        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: "auto" }}>
          <PoolDataPoint
            label="Acoperit din vânzări"
            value={formatMoney(coverage.coveredInterest)}
            color="#067647"
          />
          <Box sx={{ textAlign: "right" }}>
            <PoolDataPoint label="Scadență următoare" value={formatDate(nextDueDate)} />
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}

function FxTrendCard({ trend }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const points = Array.isArray(trend?.points)
    ? trend.points
        .map((point) => ({
          ...point,
          rateToRon: Number(point.rateToRon),
        }))
        .filter((point) => Number.isFinite(point.rateToRon))
    : [];

  if (!points.length) return null;

  const chartWidth = 166;
  const chartHeight = 42;
  const padX = 8;
  const padTop = 7;
  const padBottom = 9;
  const rates = points.map((point) => point.rateToRon);
  const minRate = Math.min(...rates);
  const maxRate = Math.max(...rates);
  const range = maxRate - minRate || 0.0001;
  const activeIndex = points.findIndex((point) => point.isCurrent);
  const currentIndex = activeIndex >= 0 ? activeIndex : points.length - 1;
  const coords = points.map((point, index) => {
    const x =
      points.length === 1
        ? chartWidth / 2
        : padX + (index * (chartWidth - padX * 2)) / (points.length - 1);
    const y =
      padTop +
      ((maxRate - point.rateToRon) / range) *
        (chartHeight - padTop - padBottom);
    return { x, y };
  });
  const linePath = coords
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(" ");
  const baseY = chartHeight - padBottom;
  const areaPath = `${linePath} L ${coords[coords.length - 1].x.toFixed(
    1
  )} ${baseY} L ${coords[0].x.toFixed(1)} ${baseY} Z`;
  const currentPoint = coords[currentIndex] || coords[coords.length - 1];
  const currentRate = points[currentIndex]?.rateToRon || rates[rates.length - 1];
  const tooltipPoint = hoverIndex === null ? null : coords[hoverIndex];
  const tooltipData = hoverIndex === null ? null : points[hoverIndex];
  const delta = currentRate - rates[0];
  const deltaText = `${delta >= 0 ? "+" : ""}${fxPreciseFormatter.format(delta)}`;
  const deltaColor = delta >= 0 ? "#047857" : "#b42318";

  return (
    <Box
      sx={{
        minHeight: 60,
        width: { xs: "100%", md: 292 },
        px: 1.15,
        py: 0.75,
        borderRadius: "13px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.15,
        overflow: "hidden",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(239,246,255,0.96))",
        border: "1px solid rgba(147, 197, 253, 0.72)",
        boxShadow: "0 14px 30px rgba(15, 23, 42, 0.09)",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#475467",
            fontWeight: 900,
            lineHeight: 1,
            textTransform: "uppercase",
          }}
        >
          EUR / săpt.
        </Typography>
        <Typography sx={{ color: "#101828", fontSize: 18, fontWeight: 950, lineHeight: 1.35 }}>
          {fxPreciseFormatter.format(currentRate)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: deltaColor, fontWeight: 900, lineHeight: 1 }}
        >
          {deltaText}
        </Typography>
      </Box>
      <Box
        sx={{
          position: "relative",
          width: chartWidth,
          height: chartHeight,
          flex: "0 0 auto",
        }}
      >
        <Box
          component="svg"
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          aria-label="Evoluție curs EUR pe săptămână"
          sx={{ display: "block", width: "100%", height: "100%" }}
        >
          <defs>
            <linearGradient id="eurTrendArea" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0.03" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#eurTrendArea)" />
          <path
            d={linePath}
            fill="none"
            stroke="#2563eb"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="3"
          />
          <line
            x1={currentPoint.x}
            x2={currentPoint.x}
            y1="5"
            y2={baseY}
            stroke="#93c5fd"
            strokeDasharray="3 3"
            strokeWidth="1.4"
          />
          <circle
            cx={currentPoint.x}
            cy={currentPoint.y}
            r="5.4"
            fill="#ffffff"
            stroke="#2563eb"
            strokeWidth="2.6"
          />
          <circle cx={currentPoint.x} cy={currentPoint.y} r="2.3" fill="#10b981" />
          {coords.map((point, index) => (
            <circle
              key={points[index]?.date || index}
              cx={point.x}
              cy={point.y}
              r="9"
              fill="transparent"
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
            />
          ))}
        </Box>
        {tooltipData && tooltipPoint ? (
          <Box
            sx={{
              position: "absolute",
              left: `min(max(${tooltipPoint.x - 46}px, 0px), ${chartWidth - 92}px)`,
              top: Math.max(0, tooltipPoint.y - 41),
              width: 92,
              px: 0.8,
              py: 0.45,
              borderRadius: "8px",
              color: "#101828",
              background: "rgba(255,255,255,0.96)",
              border: "1px solid rgba(191,219,254,0.95)",
              boxShadow: "0 10px 22px rgba(15, 23, 42, 0.13)",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            <Typography
              sx={{
                color: "#475467",
                fontSize: 10,
                fontWeight: 900,
                lineHeight: 1.1,
                textTransform: "capitalize",
              }}
              noWrap
            >
              {formatWeekday(tooltipData.date)}
            </Typography>
            <Typography sx={{ color: "#1d4ed8", fontSize: 12, fontWeight: 950 }}>
              {fxPreciseFormatter.format(tooltipData.rateToRon)}
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
            position: "absolute",
            right: 0,
            bottom: -2,
            px: 0.6,
            py: 0.15,
            borderRadius: "999px",
            color: "#1d4ed8",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            fontSize: 10,
            fontWeight: 950,
            lineHeight: 1.25,
          }}
        >
          Azi
        </Box>
      </Box>
    </Box>
  );
}

function FieldGrid({ children }) {
  return (
    <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
      {children}
    </Grid>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        p: 2.5,
        mb: 2,
        background: "#fff",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={{ color: "#667085", mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {children}
    </Paper>
  );
}

function OperationCard({
  step,
  title,
  subtitle,
  icon,
  accent = "#2563eb",
  children,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid #e4e7ec",
        borderRadius: "12px",
        overflow: "hidden",
        background: "#fff",
        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.06)",
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          background: "#fff",
        },
        "& .MuiInputLabel-root": {
          color: "#667085",
          fontWeight: 700,
        },
        "& .MuiButton-root": {
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 800,
          boxShadow: "none",
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          borderBottom: "1px solid #eef2f6",
          background:
            "linear-gradient(135deg, rgba(248,250,252,0.96), rgba(255,255,255,0.98))",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              color: accent,
              background: `${accent}14`,
              border: `1px solid ${accent}26`,
              flex: "0 0 auto",
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
              <Chip
                size="small"
                label={step}
                sx={{
                  height: 22,
                  borderRadius: "7px",
                  color: accent,
                  background: `${accent}14`,
                  fontWeight: 900,
                }}
              />
              <Typography sx={{ fontWeight: 900, color: "#101828" }}>
                {title}
              </Typography>
            </Stack>
            {subtitle ? (
              <Typography variant="caption" sx={{ color: "#667085" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

const operateActionSx = {
  minHeight: 44,
  px: 2.25,
};

function PoolOption({ pool }) {
  return (
    <Box component="span">
      {pool.investorName} / {pool.name} / cash disponibil{" "}
      {formatMoney(pool.metrics?.cashAvailable)}
    </Box>
  );
}

function InvestitiiPage() {
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [data, setData] = useState({
    investors: [],
    pools: [],
    products: [],
    transactions: [],
    totals: {},
  });
  const [fx, setFx] = useState(null);
  const [fxTrend, setFxTrend] = useState(null);
  const [investorForm, setInvestorForm] = useState(initialInvestor);
  const [poolForm, setPoolForm] = useState(initialPool);
  const [capitalForm, setCapitalForm] = useState(initialCapital);
  const [productForm, setProductForm] = useState(initialProduct);
  const [productEditId, setProductEditId] = useState(null);
  const [productEditForm, setProductEditForm] = useState(initialProductEdit);
  const [productEditSaving, setProductEditSaving] = useState(false);
  const [productDeleteId, setProductDeleteId] = useState(null);
  const [productEditMessage, setProductEditMessage] = useState(null);
  const [skuLookupLoading, setSkuLookupLoading] = useState(false);
  const [skuLookupMessage, setSkuLookupMessage] = useState(null);
  const [saleForm, setSaleForm] = useState(initialSale);
  const [saleSyncLoading, setSaleSyncLoading] = useState(false);
  const [saleSyncMessage, setSaleSyncMessage] = useState(null);
  const [jiraSyncLoading, setJiraSyncLoading] = useState(false);
  const [wooCatalogSyncLoading, setWooCatalogSyncLoading] = useState(false);
  const [productSyncMessage, setProductSyncMessage] = useState(null);
  const [productSyncProgressText, setProductSyncProgressText] = useState("");
  const productSyncLockRef = useRef(false);
  const [refundReviewSaving, setRefundReviewSaving] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [interestForm, setInterestForm] = useState(initialInterest);

  const activePools = useMemo(
    () => data.pools.filter((pool) => pool.status === "active"),
    [data.pools]
  );
  const openProducts = useMemo(
    () =>
      data.products.filter((product) =>
        ["in_stock", "reserved"].includes(product.status)
      ),
    [data.products]
  );
  const productSearchTokens = useMemo(
    () => normalizeSearchText(productSearch).split(/\s+/).filter(Boolean),
    [productSearch]
  );
  const filteredProducts = useMemo(() => {
    if (!productSearchTokens.length) return data.products;

    return data.products.filter((product) => {
      const searchableText = [
        product.jiraJournalNumber,
        product.sku,
        product.model,
        product.brand,
      ]
        .map(normalizeSearchText)
        .join(" ");

      return productSearchTokens.every((token) =>
        searchableText.includes(token)
      );
    });
  }, [data.products, productSearchTokens]);
  const pendingRefundReviewCount = useMemo(
    () =>
      data.products.filter(
        (product) =>
          product.refundReviewPending === true ||
          Number(product.refundReviewPending) === 1
      ).length,
    [data.products]
  );
  const consignmentPurchaseCount = data.products.filter(
    isConsignmentPurchase
  ).length;
  const assignedProductBySku = useMemo(() => {
    const bySku = new Map();
    data.products.forEach((product) => {
      const sku = normalizeSku(product.sku);
      if (sku && !bySku.has(sku)) bySku.set(sku, product);
    });
    return bySku;
  }, [data.products]);
  const assignedProductForForm = assignedProductBySku.get(
    normalizeSku(productForm.sku)
  );
  const productFormUsesContract = Boolean(
    String(productForm.contractId || "").trim()
  );
  const assignedProductForEdit = assignedProductBySku.get(
    normalizeSku(productEditForm.sku)
  );
  const editSkuDuplicate =
    assignedProductForEdit &&
    Number(assignedProductForEdit.id) !== Number(productEditId);
  const totals = data.totals || {};
  const currentMonth = totals.currentMonth || {};
  const monthlyInterestDue =
    currentMonth.monthlyInterestDue ??
    currentMonth.automaticInterest ??
    currentMonth.interestExpected;
  const monthlyRealizedSalesProfit =
    currentMonth.realizedSalesProfit ?? currentMonth.realizedProfit;
  const monthlyInterestCovered =
    currentMonth.interestCoveredFromSalesProfit;
  const monthlyInterestRemaining =
    currentMonth.interestRemainingAfterSalesProfit;
  const monthlyProfitAboveInterest = currentMonth.profitAboveInterest;
  const monthlyInterestCoverage = getInterestCoverage({
    interestToCover: monthlyInterestDue,
    remainingInterest: monthlyInterestRemaining,
    coveredInterest: monthlyInterestCovered,
    realizedProfit: monthlyRealizedSalesProfit,
  });
  const effectiveMonthlyProfitAboveInterest =
    optionalNonNegativeNumber(monthlyProfitAboveInterest) ??
    Math.max(
      0,
      nonNegativeNumber(monthlyRealizedSalesProfit) -
        monthlyInterestCoverage.targetInterest
    );
  const nextInterestDueDate = useMemo(
    () =>
      activePools
        .map((pool) => pool.nextDueDate)
        .filter(Boolean)
        .sort()[0] || null,
    [activePools]
  );
  const hasInterestBalance = Number(totals.interestBalance || 0) > 0;
  const hasCurrentInterestShortfall =
    monthlyInterestCoverage.outstandingInterest > 0;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [response, fxResponse, fxTrendResponse] = await Promise.all([
        axios.get(`${INVESTITII_API}/overview`),
        axios.get(`${INVESTITII_API}/fx/eur`).catch(() => null),
        axios.get(`${INVESTITII_API}/fx/eur/week`).catch(() => null),
      ]);
      setData(response.data);
      if (fxResponse?.data) setFx(fxResponse.data);
      if (fxTrendResponse?.data) setFxTrend(fxTrendResponse.data);
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error.response?.data?.error ||
          "Nu am putut încărca modulul Investiții.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const submit = async (callback, successText) => {
    setMessage(null);
    setLoading(true);
    try {
      await callback();
      setMessage({ type: "success", text: successText });
      await loadData();
      return true;
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.error || "Operațiunea nu a reușit.",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const applyProductLookupPayload = useCallback((payload, fallback = {}) => {
    const values = payload.values || {};
    const pick = (value, fallbackValue) =>
      value === 0 || value ? String(value) : fallbackValue;

    setProductForm((previous) => ({
      ...previous,
      contractId: pick(
        values.contractId || payload.contractId || fallback.contractId,
        previous.contractId
      ),
      jiraIssueId: pick(
        values.jiraIssueId || payload.sources?.jira?.issueId,
        previous.jiraIssueId
      ),
      jiraJournalNumber: pick(
        values.jiraJournalNumber ||
          values.contractId ||
          payload.contractId ||
          payload.sources?.jira?.contractId ||
          fallback.contractId,
        previous.jiraJournalNumber
      ),
      sku: values.sku || payload.sku || fallback.sku || previous.sku,
      brand: pick(values.brand, previous.brand),
      model: pick(values.model, previous.model),
      purchasePrice: values.purchasePriceMissing
        ? ""
        : pick(values.purchasePrice, previous.purchasePrice),
      tipAchizitie: values.tipAchizitie || "",
      isConsignmentPurchase: isConsignmentPurchase(values),
      purchasePriceMissing: Boolean(values.purchasePriceMissing),
      extraCosts: pick(values.extraCosts, previous.extraCosts),
      listingPrice: pick(values.listingPrice, previous.listingPrice),
      purchaseDate: pick(values.purchaseDate, previous.purchaseDate),
      notes: pick(values.notes, previous.notes),
    }));

    const sources = [];
    if (payload.found?.jira) {
      sources.push(payload.lookupType === "contract" ? "Jira LEB" : "Jira");
    }
    if (payload.found?.woo) sources.push("Woo/products");
    if (payload.found?.ai) sources.push("OpenAI");
    const sourceText = sources.length ? sources.join(" + ") : "datele găsite";

    setSkuLookupMessage({
      type: payload.found?.alreadyTracked ? "warning" : "success",
      text: payload.found?.alreadyTracked
        ? `Am găsit date în ${sourceText}, dar SKU-ul există deja în Investiții.`
        : `Am completat datele din ${sourceText}. Verifică valorile și apasă Alocă SKU.`,
    });
  }, []);

  const createInvestor = async (event) => {
    event.preventDefault();
    const ok = await submit(
      () => axios.post(`${INVESTITII_API}/investors`, investorForm),
      "Investitorul a fost creat."
    );
    if (ok) setInvestorForm(initialInvestor);
  };

  const createPool = async (event) => {
    event.preventDefault();
    const ok = await submit(
      () => axios.post(`${INVESTITII_API}/pools`, poolForm),
      "Pool-ul de investiție a fost creat."
    );
    if (ok) setPoolForm(initialPool);
  };

  const addCapital = async (event) => {
    event.preventDefault();
    const ok = await submit(
      () =>
        axios.post(
          `${INVESTITII_API}/pools/${capitalForm.poolId}/capital`,
          capitalForm
        ),
      "Tranzacția de capital a fost înregistrată."
    );
    if (ok) setCapitalForm(initialCapital);
  };

  const lookupProductSku = useCallback(async (skuValue) => {
    const sku = String(skuValue || "").trim();
    if (!sku) return;

    setSkuLookupLoading(true);
    setSkuLookupMessage(null);

    try {
      const response = await axios.get(
        `${INVESTITII_API}/products/lookup/${encodeURIComponent(sku)}`
      );
      const payload = response.data || {};
      applyProductLookupPayload(payload, { sku });
    } catch (error) {
      setSkuLookupMessage({
        type: "error",
        text:
          error.response?.data?.error ||
          "Nu am putut găsi date pentru acest SKU.",
      });
    } finally {
      setSkuLookupLoading(false);
    }
  }, [applyProductLookupPayload]);

  const lookupProductContract = useCallback(async (contractValue) => {
    const contractId = String(contractValue || "").trim();
    if (!contractId) return;

    setSkuLookupLoading(true);
    setSkuLookupMessage(null);

    try {
      const response = await axios.get(
        `${INVESTITII_API}/products/lookup-contract/${encodeURIComponent(
          contractId
        )}`
      );
      const payload = response.data || {};
      applyProductLookupPayload(payload, { contractId });
    } catch (error) {
      setSkuLookupMessage({
        type: "error",
        text:
          error.response?.data?.error ||
          "Nu am putut găsi date pentru acest contract LEB.",
      });
    } finally {
      setSkuLookupLoading(false);
    }
  }, [applyProductLookupPayload]);

  const addProduct = async (event) => {
    event.preventDefault();
    if (assignedProductForForm) {
      setSkuLookupMessage({
        type: "warning",
        text: `Produsul ${assignedProductForForm.sku} este deja asignat în Produse finanțate cu status ${statusLabel(
          assignedProductForForm.status
        )}.`,
      });
      return;
    }
    const ok = await submit(
      () => axios.post(`${INVESTITII_API}/products`, productForm),
      "Produsul a fost alocat pe pool."
    );
    if (ok) {
      setProductForm(initialProduct);
      setSkuLookupMessage(null);
    }
  };

  const startProductEdit = (product) => {
    setProductEditId(product.id);
    setProductEditForm(buildProductEditForm(product));
    setProductEditMessage(null);
  };

  const cancelProductEdit = () => {
    setProductEditId(null);
    setProductEditForm(initialProductEdit);
    setProductEditMessage(null);
  };

  const updateProductEditField = (field, value) => {
    setProductEditForm((previous) => ({ ...previous, [field]: value }));
  };

  const saveProductEdit = async (event) => {
    event.preventDefault();
    if (!productEditId) return;
    if (editSkuDuplicate) {
      setProductEditMessage({
        type: "warning",
        text: `SKU-ul ${assignedProductForEdit.sku} este deja asignat în Produse finanțate cu status ${statusLabel(
          assignedProductForEdit.status
        )}.`,
      });
      return;
    }

    setProductEditSaving(true);
    setProductEditMessage(null);
    try {
      await axios.put(
        `${INVESTITII_API}/products/${productEditId}`,
        productEditForm
      );
      setProductEditMessage({
        type: "success",
        text: "Produsul a fost actualizat.",
      });
      setProductEditId(null);
      setProductEditForm(initialProductEdit);
      await loadData();
    } catch (error) {
      setProductEditMessage({
        type: "error",
        text:
          error.response?.data?.error ||
          "Nu am putut actualiza produsul.",
      });
    } finally {
      setProductEditSaving(false);
    }
  };

  const deleteProduct = async (product) => {
    if (!product?.id || productDeleteId) return;

    const confirmed = window.confirm(
      `Ștergi ${product.sku || "produsul"} din Produse finanțate? Se vor șterge și alocarea, tranzacțiile și vânzările asociate.`
    );
    if (!confirmed) return;

    setProductDeleteId(product.id);
    setProductEditMessage(null);
    try {
      const deleteResponse = await axios.delete(
        `${INVESTITII_API}/products/${product.id}`
      );
      emitInvestitiiProductDeleted({
        id: product.id,
        productId: deleteResponse.data?.productId,
        sku: deleteResponse.data?.sku || product.sku,
      });
      if (Number(productEditId) === Number(product.id)) {
        setProductEditId(null);
        setProductEditForm(initialProductEdit);
      }
      setProductEditMessage({
        type: "success",
        text: `Produsul ${product.sku || ""} a fost șters și dezalocat din Autentificare.`,
      });
      await loadData();
    } catch (error) {
      setProductEditMessage({
        type: "error",
        text:
          error.response?.data?.error ||
          "Nu am putut șterge produsul.",
      });
    } finally {
      setProductDeleteId(null);
    }
  };

  const sellProduct = async (event) => {
    event.preventDefault();
    const ok = await submit(
      () =>
        axios.patch(
          `${INVESTITII_API}/products/${saleForm.productId}/sale`,
          saleForm
        ),
      "Vânzarea a fost înregistrată."
    );
    if (ok) setSaleForm(initialSale);
  };

  const syncSalesFromOrders = async () => {
    setSaleSyncLoading(true);
    setSaleSyncMessage(null);
    try {
      const response = await axios.post(
        `${INVESTITII_API}/products/sync-sales-from-orders`
      );
      const result = response.data || {};
      const summary = wooOrdersSyncSummary(result);

      setSaleSyncMessage({
        type: summary.type,
        text: summary.text,
      });
      await loadData();
    } catch (error) {
      setSaleSyncMessage({
        type: "error",
        text:
          error.response?.data?.error ||
          "Nu am putut sincroniza vânzările din comenzi.",
      });
    } finally {
      setSaleSyncLoading(false);
    }
  };

  const syncProductsFromJira = async () => {
    if (productSyncLockRef.current) return;
    productSyncLockRef.current = true;
    setJiraSyncLoading(true);
    setProductSyncMessage(null);
    setProductSyncProgressText(
      `Citesc ${data.products.length} produse și actualizez datele din Jira...`
    );
    try {
      const response = await axios.post(`${INVESTITII_API}/products/sync-jira`);
      const result = response.data || {};
      const updated = Number(result.updatedCount || 0);
      const unchanged = Number(result.unchangedCount || 0);
      const skipped = Number(result.skippedCount || 0);
      const checked = Number(result.checked || 0);
      const updatedList = (result.synced || [])
        .filter((item) => item.status === "updated")
        .slice(0, 6)
        .map((item) =>
          item.oldSku && item.oldSku !== item.sku
            ? `${item.oldSku} → ${item.sku}`
            : item.sku
        )
        .join(", ");

      setProductSyncMessage({
        open: true,
        type: skipped > 0 && updated === 0 ? "warning" : "success",
        text:
          `Sync Jira complet: ${updated} actualizate, ${unchanged} neschimbate, ${skipped} sărite din ${checked}.` +
          (updatedList ? ` Actualizate: ${updatedList}.` : ""),
      });
      setProductSyncProgressText("Reîncarc lista actualizată...");
      await loadData();
    } catch (error) {
      setProductSyncMessage({
        open: true,
        type: "error",
        text:
          error.response?.data?.error ||
          "Nu am putut sincroniza produsele din Jira.",
      });
    } finally {
      setJiraSyncLoading(false);
      setProductSyncProgressText("");
      productSyncLockRef.current = false;
    }
  };

  const refreshWooCatalog = async () => {
    if (productSyncLockRef.current) return;
    productSyncLockRef.current = true;
    setWooCatalogSyncLoading(true);
    setProductSyncMessage(null);
    setProductSyncProgressText(
      "Recitesc întreg catalogul direct din Woo și actualizez tabela products..."
    );

    let catalogRefreshed = false;
    try {
      const catalogResponse = await axios.get(WOO_CATALOG_SYNC_API, {
        timeout: 0,
      });
      catalogRefreshed = true;
      setProductSyncProgressText(
        "Catalog actualizat. Verific comenzile Woo pentru vânzări și retururi..."
      );
      const ordersResponse = await axios.post(
        `${INVESTITII_API}/products/sync-sales-from-orders`,
        {},
        { timeout: 0 }
      );
      const summary = wooOrdersSyncSummary(ordersResponse.data || {});
      setProductSyncMessage({
        open: true,
        type: summary.type,
        text: `${
          catalogResponse.data?.message ||
          "Catalogul Woo a fost reîmprospătat complet."
        } ${summary.text}`,
      });
      setProductSyncProgressText(
        "Reîncarc produsele cu vânzările și retururile actualizate..."
      );
      await loadData();
    } catch (error) {
      const responseMessage =
        typeof error.response?.data === "string"
          ? error.response.data
          : error.response?.data?.error;
      setProductSyncMessage({
        open: true,
        type: catalogRefreshed ? "warning" : "error",
        text:
          responseMessage ||
          (catalogRefreshed
            ? "Catalogul Woo a fost actualizat, dar nu am putut verifica vânzările și retururile."
            : "Nu am putut reîmprospăta catalogul complet din Woo."),
      });
      if (catalogRefreshed) {
        setProductSyncProgressText(
          "Reîncarc produsele din catalogul actualizat..."
        );
        await loadData();
      }
    } finally {
      setWooCatalogSyncLoading(false);
      setProductSyncProgressText("");
      productSyncLockRef.current = false;
    }
  };

  const resolveRefundReview = async (product, returned) => {
    if (!product?.id || !product.refundOrderId || refundReviewSaving) return;

    setRefundReviewSaving({ productId: product.id, returned });
    try {
      await axios.patch(
        `${INVESTITII_API}/products/${product.id}/refund-review`,
        {
          orderId: product.refundOrderId,
          returned,
        }
      );
      setProductSyncMessage({
        open: true,
        type: "success",
        text: returned
          ? `Produsul ${product.sku} a fost repus în stoc.`
          : `Produsul ${product.sku} rămâne vândut.`,
      });
      await loadData();
    } catch (error) {
      setProductSyncMessage({
        open: true,
        type: "error",
        text:
          error.response?.data?.error ||
          "Nu am putut salva decizia pentru retur.",
      });
    } finally {
      setRefundReviewSaving(null);
    }
  };

  const closeProductSyncSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setProductSyncMessage((previous) =>
      previous ? { ...previous, open: false } : previous
    );
  };

  const addInterest = async (event) => {
    event.preventDefault();
    const ok = await submit(
      () =>
        axios.post(
          `${INVESTITII_API}/pools/${interestForm.poolId}/interest`,
          interestForm
        ),
      interestForm.action === "due"
        ? "Dobânda datorată a fost marcată."
        : "Plata dobânzii a fost înregistrată."
    );
    if (ok) setInterestForm(initialInterest);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, color: "#111827" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" variant="h4" sx={{ color: "#101828", fontWeight: 900 }}>
            Investiții
          </Typography>
          <Typography variant="body2" sx={{ color: "#667085", mt: 0.5 }}>
            Ledger în EUR pe investitori și pool-uri; prețurile produselor se
            introduc în RON și sunt convertite automat cu BNR.
          </Typography>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={0.75}
            sx={{ mt: 1.25 }}
          >
            <Chip
              size="small"
              icon={<AccountBalanceWalletIcon fontSize="small" />}
              label={`${activePools.length} pool-uri active`}
              sx={{
                width: "fit-content",
                height: 28,
                borderRadius: "6px",
                color: "#1d4ed8",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                fontWeight: 800,
              }}
            />
            <Chip
              size="small"
              icon={<Inventory2Icon fontSize="small" />}
              label={`${openProducts.length} produse în stoc`}
              sx={{
                width: "fit-content",
                height: 28,
                borderRadius: "6px",
                color: "#0f766e",
                background: "#f0fdfa",
                border: "1px solid #99f6e4",
                fontWeight: 800,
              }}
            />
          </Stack>
        </Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ width: { xs: "100%", md: "auto" } }}
        >
          <Tooltip title={loading ? "Actualizez..." : "Reîncarcă"}>
            <span>
              <IconButton
                color="primary"
                onClick={loadData}
                disabled={loading}
                aria-label="Reîncarcă"
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "14px",
                  color: "#fff",
                  background:
                    "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)",
                  boxShadow: "0 14px 30px rgba(37, 99, 235, 0.22)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0284c7 0%, #1d4ed8 100%)",
                    boxShadow: "0 16px 34px rgba(37, 99, 235, 0.28)",
                  },
                  "&.Mui-disabled": {
                    color: "#e0f2fe",
                    background:
                      "linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)",
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <FxTrendCard trend={fxTrend} />
        </Stack>
      </Stack>

      {message ? (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      ) : null}

      <Box component="section" sx={{ mb: 3.5 }}>
        <DashboardSectionHeader
          eyebrow="Portofoliu"
          title="Capital și stoc"
          helper="Disponibilitatea și costul produselor finanțate."
        />
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6} lg={3}>
            <DashboardMetric
              label="Capital primit"
              value={formatMoney(totals.capitalIn)}
              helper={`${activePools.length} pool-uri active în portofoliu`}
              icon={<AccountBalanceWalletIcon fontSize="small" />}
              tone="blue"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DashboardMetric
              label="Cash disponibil"
              value={formatMoney(totals.cashAvailable)}
              helper="Disponibil pentru alocări noi de produse."
              icon={<PaidIcon fontSize="small" />}
              tone="teal"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DashboardMetric
              label="Cost total cumpărat"
              value={formatMoney(totals.investedInStock)}
              helper="Include și produsele deja vândute."
              icon={<ReceiptLongIcon fontSize="small" />}
              tone="coral"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DashboardMetric
              label="Stoc real"
              value={formatMoney(totals.stockCost)}
              helper={`${openProducts.length} produse finanțate sunt în stoc.`}
              icon={<Inventory2Icon fontSize="small" />}
              tone="teal"
            />
          </Grid>
        </Grid>
      </Box>

      <Box component="section" sx={{ mb: 3.5 }}>
        <DashboardSectionHeader
          eyebrow="Randament"
          title="Performanță Luxura"
          helper="Dobânda se acoperă înaintea profitului net Luxura."
        />
        <Grid container spacing={1.5} alignItems="stretch">
          <Grid item xs={12}>
            <MonthlyProfitBalance
              interestToCover={monthlyInterestDue}
              remainingInterest={monthlyInterestRemaining}
              interestCovered={monthlyInterestCovered}
              netProfit={effectiveMonthlyProfitAboveInterest}
              realizedProfit={monthlyRealizedSalesProfit}
            />
          </Grid>
          <Grid item xs={12} lg={8}>
            <ProfitSummaryPanel
              currentNet={formatMoney(effectiveMonthlyProfitAboveInterest)}
              totalNet={formatMoney(totals.profitLuxuraNet)}
              currentProfit={formatMoney(monthlyRealizedSalesProfit)}
              totalProfit={formatMoney(totals.realizedProfit)}
              unrealizedProfit={formatMoney(totals.unrealizedProfit)}
              hasInterestBalance={hasInterestBalance || hasCurrentInterestShortfall}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <InterestSummaryPanel
              interestToCover={monthlyInterestDue}
              balance={monthlyInterestRemaining}
              coveredInterest={monthlyInterestCovered}
              realizedProfit={monthlyRealizedSalesProfit}
              nextDueDate={nextInterestDueDate}
            />
          </Grid>
        </Grid>
      </Box>

      <Paper
        elevation={0}
        sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", mb: 2 }}
      >
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
          <Tab value="overview" label="Overview" />
          <Tab value="operate" label="Operare" />
          <Tab value="products" label="Produse" />
          <Tab value="ledger" label="Ledger" />
        </Tabs>
      </Paper>

      {tab === "overview" && (
        <Box component="section" sx={{ mb: 2 }}>
          <DashboardSectionHeader
            eyebrow="Detaliere"
            title="Pool-uri de investiție"
            helper="Capital, stoc, randament și dobândă într-un singur rând de control."
          />
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: "1px solid #e4e7ec",
              borderRadius: "8px",
              boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Table size="small" aria-label="Pool-uri de investiție" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& .MuiTableCell-root": {
                      py: 1.25,
                      color: "#667085",
                      background: "#f8fafc",
                      borderBottom: "1px solid #e4e7ec",
                      fontSize: 11,
                      fontWeight: 900,
                      letterSpacing: 0,
                      textTransform: "uppercase",
                    },
                  }}
                >
                  <TableCell>Pool</TableCell>
                  <TableCell>Capital</TableCell>
                  <TableCell>Stoc</TableCell>
                  <TableCell>Performanță</TableCell>
                  <TableCell>Dobândă</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.pools.map((pool) => {
                  const isActive = pool.status === "active";
                  const statusColor = isActive ? "#067647" : "#667085";
                  const statusBackground = isActive ? "#ecfdf3" : "#f2f4f7";

                  return (
                    <TableRow
                      key={pool.id}
                      hover
                      sx={{
                        "& .MuiTableCell-root": {
                          py: 1.75,
                          verticalAlign: "top",
                          borderBottom: "1px solid #eef2f6",
                        },
                        "&:last-child .MuiTableCell-root": { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={{ minWidth: 210 }}>
                        <Typography sx={{ color: "#101828", fontWeight: 900 }}>
                          {pool.investorName}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                          <Typography variant="body2" sx={{ color: "#475467", fontWeight: 700 }}>
                            {pool.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={statusLabel(pool.status)}
                            sx={{
                              height: 22,
                              borderRadius: "5px",
                              color: statusColor,
                              background: statusBackground,
                              fontSize: 11,
                              fontWeight: 800,
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Stack spacing={1.1}>
                          <PoolDataPoint
                            label="Capital primit"
                            value={formatMoney(pool.metrics.capitalIn)}
                          />
                          <PoolDataPoint
                            label="Cash disponibil"
                            value={formatMoney(pool.metrics.cashAvailable)}
                            color="#0f766e"
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 155 }}>
                        <Stack spacing={1.1}>
                          <PoolDataPoint
                            label="Cost total cumpărat"
                            value={formatMoney(pool.metrics.investedInStock)}
                          />
                          <PoolDataPoint
                            label="Stoc real"
                            value={formatMoney(pool.metrics.stockCost)}
                            color="#0f766e"
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <Stack spacing={1.1}>
                          <PoolDataPoint
                            label="Valoare estimată"
                            value={formatMoney(pool.metrics.estimatedStockValue)}
                          />
                          <PoolDataPoint
                            label="Profit realizat / nerealizat"
                            value={`${formatMoney(pool.metrics.realizedProfit)} / ${formatMoney(
                              pool.metrics.unrealizedProfit
                            )}`}
                            color="#2563eb"
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Stack spacing={1.1}>
                          <PoolDataPoint
                            label="Lunar"
                            value={formatMoney(pool.monthlyInterest)}
                            color="#b45309"
                          />
                          <PoolDataPoint
                            label="Următoarea scadență"
                            value={formatDate(pool.nextDueDate)}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!data.pools.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#667085" }}>
                      Nu există pool-uri încă.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === "operate" && (
        <Grid container spacing={2.5} alignItems="stretch">
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #dbeafe",
                borderRadius: "12px",
                p: { xs: 2, md: 2.5 },
                background:
                  "linear-gradient(135deg, rgba(239,246,255,0.95), rgba(255,255,255,0.98))",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography sx={{ fontWeight: 900, color: "#101828" }}>
                    Operare investiții
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#475467", mt: 0.5 }}>
                    Flux rapid pentru capital, produse finanțate, dobânzi și vânzări.
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Chip
                    label="Ledger EUR"
                    sx={{ fontWeight: 800, background: "#fff", border: "1px solid #bfdbfe" }}
                  />
                  <Chip
                    label="Produse în RON"
                    sx={{ fontWeight: 800, background: "#fff", border: "1px solid #bfdbfe" }}
                  />
                  {fx ? (
                    <Chip
                      label={`BNR ${numberFormatter.format(fx.rateToRon)} RON`}
                      sx={{ fontWeight: 800, background: "#eff6ff", color: "#1d4ed8" }}
                    />
                  ) : null}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={6}>
            <OperationCard
              step="01"
              title="Adaugă investitor"
              subtitle="Datele principale pentru contract și evidență."
              icon={<PersonAddAlt1Icon fontSize="small" />}
              accent="#2563eb"
            >
              <Box component="form" onSubmit={createInvestor}>
                <FieldGrid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Nume"
                      value={investorForm.name}
                      onChange={(e) =>
                        setInvestorForm({ ...investorForm, name: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={investorForm.phone}
                      onChange={(e) =>
                        setInvestorForm({ ...investorForm, phone: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={investorForm.email}
                      onChange={(e) =>
                        setInvestorForm({ ...investorForm, email: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="IBAN"
                      value={investorForm.iban}
                      onChange={(e) =>
                        setInvestorForm({ ...investorForm, iban: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Contract / observații"
                      value={investorForm.contractRef}
                      onChange={(e) =>
                        setInvestorForm({
                          ...investorForm,
                          contractRef: e.target.value,
                        })
                      }
                    />
                  </Grid>
                </FieldGrid>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2, ...operateActionSx }}
                  disabled={loading}
                >
                  Creează investitor
                </Button>
              </Box>
            </OperationCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <OperationCard
              step="02"
              title="Creează pool de investiție"
              subtitle="Capital inițial și dobândă lunară, cu ziua de plată configurabilă."
              icon={<AccountBalanceWalletIcon fontSize="small" />}
              accent="#0f766e"
            >
              <Box component="form" onSubmit={createPool}>
                <FieldGrid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Investitor</InputLabel>
                      <Select
                        label="Investitor"
                        value={poolForm.investorId}
                        onChange={(e) =>
                          setPoolForm({ ...poolForm, investorId: e.target.value })
                        }
                      >
                        {data.investors.map((investor) => (
                          <MenuItem key={investor.id} value={investor.id}>
                            {investor.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Nume pool"
                      value={poolForm.name}
                      onChange={(e) =>
                        setPoolForm({ ...poolForm, name: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Capital inițial"
                      value={poolForm.initialCapital}
                      onChange={(e) =>
                        setPoolForm({
                          ...poolForm,
                          initialCapital: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Dobândă lunară"
                      value={poolForm.monthlyInterest}
                      onChange={(e) =>
                        setPoolForm({
                          ...poolForm,
                          monthlyInterest: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Zi plată dobândă"
                      value={poolForm.dueDay}
                      onChange={(e) =>
                        setPoolForm({ ...poolForm, dueDay: e.target.value })
                      }
                      helperText="Ziua lunară în care dobânda devine datorată"
                      inputProps={{ min: 1, max: 28, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Data primirii"
                      InputLabelProps={{ shrink: true }}
                      value={poolForm.receivedDate}
                      onChange={(e) =>
                        setPoolForm({
                          ...poolForm,
                          receivedDate: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Observații"
                      value={poolForm.notes}
                      onChange={(e) =>
                        setPoolForm({ ...poolForm, notes: e.target.value })
                      }
                    />
                  </Grid>
                </FieldGrid>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<PaidIcon />}
                  sx={{ mt: 2, ...operateActionSx }}
                  disabled={loading || !data.investors.length}
                >
                  Creează pool
                </Button>
              </Box>
            </OperationCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <OperationCard
              step="03"
              title="Capital suplimentar / retras"
              subtitle="Ajustează capitalul disponibil al unui pool activ."
              icon={<PaidIcon fontSize="small" />}
              accent="#7c3aed"
            >
              <Box component="form" onSubmit={addCapital}>
                <FieldGrid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Pool</InputLabel>
                      <Select
                        label="Pool"
                        value={capitalForm.poolId}
                        onChange={(e) =>
                          setCapitalForm({
                            ...capitalForm,
                            poolId: e.target.value,
                          })
                        }
                      >
                        {activePools.map((pool) => (
                          <MenuItem key={pool.id} value={pool.id}>
                            <PoolOption pool={pool} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Tip</InputLabel>
                      <Select
                        label="Tip"
                        value={capitalForm.direction}
                        onChange={(e) =>
                          setCapitalForm({
                            ...capitalForm,
                            direction: e.target.value,
                          })
                        }
                      >
                        <MenuItem value="in">Capital primit</MenuItem>
                        <MenuItem value="out">Capital retras</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Sumă"
                      value={capitalForm.amount}
                      onChange={(e) =>
                        setCapitalForm({ ...capitalForm, amount: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Dată"
                      InputLabelProps={{ shrink: true }}
                      value={capitalForm.date}
                      onChange={(e) =>
                        setCapitalForm({ ...capitalForm, date: e.target.value })
                      }
                    />
                  </Grid>
                </FieldGrid>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ mt: 2, ...operateActionSx }}
                  disabled={loading || !activePools.length}
                >
                  Înregistrează capital
                </Button>
              </Box>
            </OperationCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <OperationCard
              step="04"
              title="Corecție dobândă"
              subtitle="Profitul calculează automat dobânda din ziua configurată; folosește doar pentru corecții de ledger."
              icon={<EventRepeatIcon fontSize="small" />}
              accent="#b45309"
            >
              <Box component="form" onSubmit={addInterest}>
                <FieldGrid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Pool</InputLabel>
                      <Select
                        label="Pool"
                        value={interestForm.poolId}
                        onChange={(e) => {
                          const selectedPool = activePools.find(
                            (pool) => Number(pool.id) === Number(e.target.value)
                          );
                          setInterestForm({
                            ...interestForm,
                            poolId: e.target.value,
                            amount:
                              interestForm.amount ||
                              selectedPool?.monthlyInterest ||
                              "",
                          });
                        }}
                      >
                        {activePools.map((pool) => (
                          <MenuItem key={pool.id} value={pool.id}>
                            <PoolOption pool={pool} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Acțiune</InputLabel>
                      <Select
                        label="Acțiune"
                        value={interestForm.action}
                        onChange={(e) =>
                          setInterestForm({
                            ...interestForm,
                            action: e.target.value,
                          })
                        }
                      >
                        <MenuItem value="due">Datorată</MenuItem>
                        <MenuItem value="paid">Plătită</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Sumă"
                      value={interestForm.amount}
                      onChange={(e) =>
                        setInterestForm({
                          ...interestForm,
                          amount: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Dată"
                      InputLabelProps={{ shrink: true }}
                      value={interestForm.date}
                      onChange={(e) =>
                        setInterestForm({ ...interestForm, date: e.target.value })
                      }
                    />
                  </Grid>
                </FieldGrid>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ mt: 2, ...operateActionSx }}
                  disabled={loading || !activePools.length}
                >
                  Salvează dobânda
                </Button>
              </Box>
            </OperationCard>
          </Grid>

          <Grid item xs={12}>
            <OperationCard
              step="05"
              title="Adaugă produs / SKU finanțat"
              subtitle="Caută după contract LEB sau SKU, completează datele și alocă produsul pe pool."
              icon={<Inventory2Icon fontSize="small" />}
              accent="#0891b2"
            >
              <Box component="form" onSubmit={addProduct}>
                <FieldGrid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth required>
                      <InputLabel>Finanțat din</InputLabel>
                      <Select
                        label="Finanțat din"
                        value={productForm.poolId}
                        onChange={(e) =>
                          setProductForm({
                            ...productForm,
                            poolId: e.target.value,
                          })
                        }
                      >
                        {activePools.map((pool) => (
                          <MenuItem key={pool.id} value={pool.id}>
                            <PoolOption pool={pool} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="ID contract Jira LEB"
                      value={productForm.contractId}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          contractId: e.target.value,
                        })
                      }
                      onBlur={(e) => lookupProductContract(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          lookupProductContract(e.currentTarget.value);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      type="button"
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => lookupProductContract(productForm.contractId)}
                      disabled={
                        skuLookupLoading ||
                        !String(productForm.contractId || "").trim()
                      }
                      sx={{ minHeight: 56 }}
                    >
                      {skuLookupLoading ? "Caut..." : "Caută contract"}
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      required={!productFormUsesContract}
                      error={Boolean(assignedProductForForm)}
                      helperText={
                        assignedProductForForm
                          ? `SKU deja asignat în Produse finanțate (${statusLabel(
                              assignedProductForForm.status
                            )})`
                          : productFormUsesContract
                            ? "Opțional când folosești ID contract"
                            : ""
                      }
                      label="SKU"
                      value={productForm.sku}
                      onChange={(e) =>
                        setProductForm({ ...productForm, sku: e.target.value })
                      }
                      onBlur={(e) => lookupProductSku(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          lookupProductSku(e.currentTarget.value);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      type="button"
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => lookupProductSku(productForm.sku)}
                      disabled={
                        skuLookupLoading ||
                        !String(productForm.sku || "").trim()
                      }
                      sx={{ minHeight: 56 }}
                    >
                      {skuLookupLoading ? "Caut..." : "Caută SKU"}
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Brand"
                      value={productForm.brand}
                      onChange={(e) =>
                        setProductForm({ ...productForm, brand: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Model"
                      value={productForm.model}
                      onChange={(e) =>
                        setProductForm({ ...productForm, model: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Cost achiziție RON"
                      value={productForm.purchasePrice}
                      InputProps={{ readOnly: productForm.isConsignmentPurchase }}
                      error={productForm.purchasePriceMissing}
                      helperText={
                        productForm.purchasePriceMissing
                          ? "Completează CNcuVD_suma în Jira și reîncarcă produsul."
                          : productForm.isConsignmentPurchase
                          ? "CNcuVD_suma · preț convenit la finalul consignației"
                          : ""
                      }
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          purchasePrice: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Costuri extra RON"
                      value={productForm.extraCosts}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          extraCosts: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Preț listare RON"
                      value={productForm.listingPrice}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          listingPrice: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Data cumpărării"
                      InputLabelProps={{ shrink: true }}
                      value={productForm.purchaseDate}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          purchaseDate: e.target.value,
                        })
                      }
                    />
                  </Grid>
                </FieldGrid>
                {skuLookupMessage ? (
                  <Alert severity={skuLookupMessage.type} sx={{ mt: 2 }}>
                    {skuLookupMessage.text}
                  </Alert>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Inventory2Icon />}
                  sx={{ mt: 2, ...operateActionSx }}
                  disabled={
                    loading ||
                    !activePools.length ||
                    productForm.purchasePriceMissing ||
                    Boolean(assignedProductForForm)
                  }
                >
                  Alocă produs
                </Button>
              </Box>
            </OperationCard>
          </Grid>

          <Grid item xs={12}>
            <OperationCard
              step="06"
              title="Înregistrează vânzare produs"
              subtitle="Marchează manual sau sincronizează vânzările din comenzile Woo."
              icon={<TrendingUpIcon fontSize="small" />}
              accent="#16a34a"
            >
              <Box component="form" onSubmit={sellProduct}>
                <FieldGrid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Produs</InputLabel>
                      <Select
                        label="Produs"
                        value={saleForm.productId}
                        onChange={(e) =>
                          setSaleForm({ ...saleForm, productId: e.target.value })
                        }
                      >
                        {openProducts.map((product) => (
                          <MenuItem key={product.id} value={product.id}>
                            {product.sku} · {product.brand} {product.model}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Preț vânzare RON"
                      value={saleForm.salePrice}
                      onChange={(e) =>
                        setSaleForm({ ...saleForm, salePrice: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Comision RON"
                      value={saleForm.commission}
                      onChange={(e) =>
                        setSaleForm({ ...saleForm, commission: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Încasare reală RON"
                      value={saleForm.receivedAmount}
                      onChange={(e) =>
                        setSaleForm({
                          ...saleForm,
                          receivedAmount: e.target.value,
                        })
                      }
                      helperText="Gol = preț - comision, în RON"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Data vânzării"
                      InputLabelProps={{ shrink: true }}
                      value={saleForm.saleDate}
                      onChange={(e) =>
                        setSaleForm({ ...saleForm, saleDate: e.target.value })
                      }
                    />
                  </Grid>
                </FieldGrid>
                {saleSyncMessage ? (
                  <Alert severity={saleSyncMessage.type} sx={{ mt: 2 }}>
                    {saleSyncMessage.text}
                  </Alert>
                ) : null}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !openProducts.length}
                    sx={operateActionSx}
                  >
                    Marchează vândut
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<ReceiptLongIcon />}
                    onClick={syncSalesFromOrders}
                    disabled={
                      loading || saleSyncLoading || !data.products.length
                    }
                    sx={operateActionSx}
                  >
                    {saleSyncLoading
                      ? "Verific comenzi..."
                      : "Marchează automat din comenzi Woo"}
                  </Button>
                </Stack>
              </Box>
            </OperationCard>
          </Grid>
        </Grid>
      )}

      {tab === "products" && (
        <Section title="Produse finanțate">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: "12px",
              border: "1px solid #dbeafe",
              background:
                "linear-gradient(135deg, rgba(239,246,255,0.92), rgba(255,255,255,0.98))",
            }}
          >
            <Box>
              <Typography sx={{ color: "#101828", fontWeight: 900 }}>
                Sincronizare produse Jira & Woo
              </Typography>
              <Typography variant="caption" sx={{ color: "#667085" }}>
                Jira actualizează produsele finanțate, iar Woo reîncarcă integral
                tabela locală de produse din catalogul live.
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              sx={{ flexShrink: 0 }}
            >
              <Button
                type="button"
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={syncProductsFromJira}
                disabled={
                  jiraSyncLoading ||
                  wooCatalogSyncLoading ||
                  loading ||
                  !data.products.length
                }
                sx={{
                  minHeight: 42,
                  px: 2.25,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 900,
                  color: "#fff",
                  background:
                    "linear-gradient(135deg, #0891b2 0%, #2563eb 100%)",
                  boxShadow: "0 12px 24px rgba(37,99,235,0.18)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0e7490 0%, #1d4ed8 100%)",
                  },
                }}
              >
                {jiraSyncLoading ? "Sincronizez Jira..." : "Sync Jira"}
              </Button>
              <Button
                type="button"
                variant="contained"
                startIcon={<CloudSyncIcon />}
                onClick={refreshWooCatalog}
                disabled={
                  loading || jiraSyncLoading || wooCatalogSyncLoading
                }
                sx={{
                  minHeight: 42,
                  px: 2.25,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 900,
                  color: "#fff",
                  whiteSpace: "nowrap",
                  background:
                    "linear-gradient(135deg, #96588a 0%, #7f54b3 100%)",
                  boxShadow: "0 12px 24px rgba(127,84,179,0.2)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #7f4775 0%, #674399 100%)",
                  },
                }}
              >
                {wooCatalogSyncLoading
                  ? "Reîmprospătez Woo..."
                  : "Reîmprospătează catalogul Woo"}
              </Button>
            </Stack>
          </Stack>
          {jiraSyncLoading || wooCatalogSyncLoading ? (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: "12px",
                border: `1px solid ${
                  wooCatalogSyncLoading ? "#e9d5ff" : "#bfdbfe"
                }`,
                background: wooCatalogSyncLoading
                  ? "rgba(250,245,255,0.9)"
                  : "rgba(239,246,255,0.88)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={0.75}
                sx={{ mb: 1 }}
              >
                <Typography
                  sx={{
                    color: wooCatalogSyncLoading ? "#7e22ce" : "#1d4ed8",
                    fontWeight: 900,
                  }}
                >
                  {wooCatalogSyncLoading
                    ? "Reîmprospătare catalog Woo în curs"
                    : "Sync Jira în curs"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#475467" }}>
                  {productSyncProgressText}
                </Typography>
              </Stack>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: "999px",
                  backgroundColor: wooCatalogSyncLoading
                    ? "rgba(233, 213, 255, 0.75)"
                    : "rgba(191, 219, 254, 0.72)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: "999px",
                    background: wooCatalogSyncLoading
                      ? "linear-gradient(135deg, #a855f7 0%, #7f54b3 100%)"
                      : "linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)",
                  },
                }}
              />
            </Box>
          ) : null}
          {productEditMessage ? (
            <Alert severity={productEditMessage.type} sx={{ mb: 2 }}>
              {productEditMessage.text}
            </Alert>
          ) : null}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <TextField
              fullWidth
              size="small"
              label="Caută produs finanțat"
              placeholder="Nr jurnal, SKU, model sau brand"
              value={productSearch}
              onChange={(event) => setProductSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: productSearch ? (
                  <InputAdornment position="end">
                    <Tooltip title="Șterge căutarea">
                      <IconButton
                        aria-label="Șterge căutarea"
                        edge="end"
                        size="small"
                        onClick={() => setProductSearch("")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                maxWidth: { md: 560 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  background: "#fff",
                },
              }}
            />
            <Chip
              label={
                productSearchTokens.length
                  ? `${filteredProducts.length} / ${data.products.length} produse`
                  : `${data.products.length} produse`
              }
              sx={{
                alignSelf: { xs: "flex-start", md: "center" },
                borderRadius: "8px",
                border: "1px solid #d0d5dd",
                background: "#fff",
                color: "#344054",
                fontWeight: 800,
              }}
            />
          </Stack>
          {productEditId ? (
            <Box
              component="form"
              onSubmit={saveProductEdit}
              sx={{
                mb: 2.5,
                border: "1px solid #c7d2fe",
                borderRadius: "14px",
                overflow: "hidden",
                background: "#fff",
                boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  background: "#fff",
                },
                "& .MuiInputLabel-root": {
                  color: "#667085",
                  fontWeight: 700,
                },
                "& .MuiButton-root": {
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 900,
                  boxShadow: "none",
                },
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.75,
                  borderBottom: "1px solid #e0e7ff",
                  background:
                    "linear-gradient(135deg, rgba(238,242,255,0.95), rgba(255,255,255,0.98))",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "11px",
                        display: "grid",
                        placeItems: "center",
                        color: "#2563eb",
                        background: "#dbeafe",
                        border: "1px solid #bfdbfe",
                        flex: "0 0 auto",
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: "#101828" }}>
                        Editează produs finanțat
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#667085" }}>
                        Actualizează datele, prețurile și statusul produsului.
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={productEditForm.sku || "SKU"}
                    sx={{
                      borderRadius: "9px",
                      background: "#fff",
                      border: "1px solid #bfdbfe",
                      color: "#1d4ed8",
                      fontWeight: 900,
                    }}
                  />
                </Stack>
              </Box>
              <Box sx={{ p: 2 }}>
              <FieldGrid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    label="SKU"
                    value={productEditForm.sku}
                    error={Boolean(editSkuDuplicate)}
                    helperText={
                      editSkuDuplicate
                        ? `SKU deja asignat (${statusLabel(
                            assignedProductForEdit.status
                          )})`
                        : ""
                    }
                    onChange={(event) =>
                      updateProductEditField("sku", event.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Brand"
                    value={productEditForm.brand}
                    onChange={(event) =>
                      updateProductEditField("brand", event.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Denumire / model"
                    value={productEditForm.model}
                    onChange={(event) =>
                      updateProductEditField("model", event.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Cost achiziție RON"
                    value={productEditForm.purchasePriceRon}
                    InputProps={{ readOnly: productEditForm.isConsignmentPurchase }}
                    error={productEditForm.purchasePriceMissing}
                    helperText={
                      productEditForm.purchasePriceMissing
                        ? "Completează CNcuVD_suma în Jira și sincronizează produsul."
                        : productEditForm.isConsignmentPurchase
                        ? "CNcuVD_suma · preț convenit la finalul consignației"
                        : ""
                    }
                    onChange={(event) =>
                      updateProductEditField(
                        "purchasePriceRon",
                        event.target.value
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Costuri extra RON"
                    value={productEditForm.extraCostsRon}
                    onChange={(event) =>
                      updateProductEditField("extraCostsRon", event.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Preț listare RON"
                    value={productEditForm.listingPriceRon}
                    onChange={(event) =>
                      updateProductEditField(
                        "listingPriceRon",
                        event.target.value
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Data cumpărării"
                    InputLabelProps={{ shrink: true }}
                    value={productEditForm.purchaseDate}
                    onChange={(event) =>
                      updateProductEditField("purchaseDate", event.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={productEditForm.status}
                      onChange={(event) =>
                        updateProductEditField("status", event.target.value)
                      }
                    >
                      {["in_stock", "reserved", "sold", "returned"].map(
                        (status) => (
                          <MenuItem key={status} value={status}>
                            {statusLabel(status)}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField
                    fullWidth
                    label="Notițe"
                    value={productEditForm.notes}
                    onChange={(event) =>
                      updateProductEditField("notes", event.target.value)
                    }
                  />
                </Grid>
              </FieldGrid>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={
                    productEditSaving ||
                    productEditForm.purchasePriceMissing ||
                    Boolean(editSkuDuplicate)
                  }
                  sx={{
                    minHeight: 44,
                    px: 2.5,
                    color: "#fff",
                    background:
                      "linear-gradient(135deg, #0891b2 0%, #2563eb 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #0e7490 0%, #1d4ed8 100%)",
                    },
                  }}
                >
                  {productEditSaving ? "Salvez..." : "Salvează"}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={cancelProductEdit}
                  disabled={productEditSaving}
                  sx={{ minHeight: 44, px: 2.5 }}
                >
                  Renunță
                </Button>
              </Stack>
              </Box>
            </Box>
          ) : null}
          {pendingRefundReviewCount > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {pendingRefundReviewCount} produs(e) din comenzi Woo cu mai multe
              articole necesită confirmare. Verifică rândurile roșii și alege
              „DA” sau „NU” la „Este returnat?”.
            </Alert>
          ) : null}
          {consignmentPurchaseCount > 0 ? (
            <Alert severity="info" icon={<EventRepeatIcon />} sx={{ mb: 2 }}>
              Produsele LP002 sunt în consignație cu promisiune de cumpărare la
              finalul perioadei de 90 / 180 de zile, conform contractului. Costul
              folosit este prețul convenit din CNcuVD_suma.
            </Alert>
          ) : null}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Nr Jurnal</TableCell>
                  <TableCell>Tip achiziție</TableCell>
                  <TableCell>Investitor / Pool</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Listare</TableCell>
                  <TableCell>Timp pe stoc</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Profit</TableCell>
                  <TableCell>ROI</TableCell>
                  <TableCell align="right">Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => {
                  const consignmentPurchase = isConsignmentPurchase(product);
                  const purchasePriceMissing =
                    consignmentPurchase && Boolean(product.purchasePriceMissing);
                  const purchasePriceCorrectionPending =
                    consignmentPurchase && Boolean(product.purchasePriceCorrectionPending);
                  const purchasePriceUnavailable =
                    purchasePriceMissing || purchasePriceCorrectionPending;
                  const refundReviewPending =
                    product.refundReviewPending === true ||
                    Number(product.refundReviewPending) === 1;
                  const restockedAfterRefund =
                    product.status === "in_stock" &&
                    Boolean(product.lastRefundRestockedAt);
                  const refundReviewSavingForProduct =
                    Number(refundReviewSaving?.productId) === Number(product.id);

                  return (
                    <TableRow
                      key={product.id}
                      hover
                      sx={{
                        ...(refundReviewPending
                          ? {
                              backgroundColor: "rgba(254, 226, 226, 0.72)",
                              "&:hover": {
                                backgroundColor:
                                  "rgba(254, 202, 202, 0.9) !important",
                              },
                              "& .MuiTableCell-root": {
                                borderBottomColor: "rgba(220, 38, 38, 0.28)",
                              },
                            }
                          : product.status === "sold"
                          ? {
                              backgroundColor: "rgba(220, 252, 231, 0.68)",
                              "&:hover": {
                                backgroundColor:
                                  "rgba(187, 247, 208, 0.9) !important",
                              },
                              "& .MuiTableCell-root": {
                                borderBottomColor: "rgba(34, 197, 94, 0.24)",
                              },
                            }
                          : consignmentPurchase
                          ? {
                              backgroundColor: "rgba(254, 243, 199, 0.65)",
                              "&:hover": {
                                backgroundColor: "rgba(253, 230, 138, 0.7) !important",
                              },
                            }
                          : {}),
                        ...(consignmentPurchase
                          ? { "& > td:first-of-type": { borderLeft: "4px solid #d97706" } }
                          : {}),
                      }}
                    >
                    <TableCell>
                      <Typography sx={{ fontWeight: 800 }}>{product.sku}</Typography>
                      <Typography variant="caption" sx={{ color: "#667085" }}>
                        {product.brand} {product.model}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 850 }}>
                        {product.jiraJournalNumber || "-"}
                      </Typography>
                      {product.jiraIssueId ? (
                        <Typography variant="caption" sx={{ color: "#667085" }}>
                          {product.jiraIssueId}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {consignmentPurchase ? (
                        <Stack spacing={0.75} sx={{ minWidth: 185 }}>
                          <Chip
                            size="small"
                            icon={<EventRepeatIcon />}
                            label="LP002 · Cumpărare la termen"
                            sx={{
                              alignSelf: "flex-start",
                              color: "#78350f",
                              backgroundColor: "#fde68a",
                              border: "1px solid #d97706",
                              fontWeight: 800,
                              "& .MuiChip-icon": { color: "#92400e" },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: "#92400e" }}>
                            {product.tipAchizitie}
                          </Typography>
                          {product.consignmentPurchaseDate ? (
                            <Typography variant="caption" sx={{ color: "#92400e" }}>
                              Termen: {formatDate(product.consignmentPurchaseDate)}
                            </Typography>
                          ) : null}
                        </Stack>
                      ) : (
                        product.tipAchizitie || "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {product.investorName || "-"}
                      <Typography variant="caption" display="block" sx={{ color: "#667085" }}>
                        {product.poolName || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {purchasePriceUnavailable ? (
                        <Typography sx={{ color: "#b42318", fontWeight: 800 }}>
                          {purchasePriceMissing
                            ? "CNcuVD_suma lipsește"
                            : "Cost de sincronizat"}
                        </Typography>
                      ) : (
                        <>
                          <Typography sx={{ fontWeight: 800 }}>
                            {formatRon(product.purchasePriceRon + product.extraCostsRon)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#667085" }}>
                            {formatMoney(product.purchasePrice + product.extraCosts)}
                          </Typography>
                        </>
                      )}
                      {consignmentPurchase ? (
                        <Typography variant="caption" display="block" sx={{ color: "#92400e" }}>
                          {purchasePriceMissing
                            ? "Completează suma în Jira"
                            : purchasePriceCorrectionPending
                            ? "Rulează Sync Jira pentru actualizarea costului"
                            : "CNcuVD_suma · preț convenit"}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800 }}>
                        {formatRon(product.listingPriceRon)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#667085" }}>
                        {formatMoney(product.listingPrice)}
                      </Typography>
                      {product.jiraWooPriceWarning ? (
                        <Tooltip
                          title={
                            product.jiraWooPriceWarning.message ||
                            "Verifică prețul din Jira și Woo."
                          }
                        >
                          <Chip
                            size="small"
                            icon={<WarningAmberIcon />}
                            label={priceWarningLabel(
                              product.jiraWooPriceWarning
                            )}
                            sx={{
                              mt: 0.75,
                              height: 24,
                              maxWidth: 210,
                              borderRadius: "7px",
                              color: "#92400e",
                              background: "#fffbeb",
                              border: "1px solid #fbbf24",
                              fontWeight: 900,
                              "& .MuiChip-icon": {
                                color: "#b45309",
                              },
                              "& .MuiChip-label": {
                                px: 0.75,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800 }}>
                        {formatStockDays(product.stockDays)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#667085" }}>
                        listat {formatDate(product.wooListingDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.75} alignItems="flex-start">
                        <Chip size="small" label={statusLabel(product.status)} />
                        {restockedAfterRefund ? (
                          <Tooltip
                            title={`Reintrat în stoc după returul Woo la ${formatDate(
                              product.lastRefundRestockedAt
                            )}`}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              icon={<EventRepeatIcon />}
                              label="Reintrat în stoc"
                              sx={{
                                height: 24,
                                color: "#6d28d9",
                                background: "rgba(250, 245, 255, 0.88)",
                                borderColor: "#d8b4fe",
                                fontWeight: 800,
                                "& .MuiChip-icon": { color: "#7c3aed" },
                              }}
                            />
                          </Tooltip>
                        ) : null}
                        {refundReviewPending ? (
                          <Box sx={{ minWidth: 190 }}>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ color: "#b42318", fontWeight: 900 }}
                            >
                              Comanda Woo #{product.refundOrderId}
                              {product.refundOrderDate
                                ? ` · ${formatDate(product.refundOrderDate)}`
                                : ""}
                            </Typography>
                            {Number(product.refundOrderProductCount || 0) > 1 ? (
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ color: "#7f1d1d" }}
                              >
                                {product.refundOrderProductCount} produse în comandă
                              </Typography>
                            ) : null}
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ mt: 0.5, color: "#7f1d1d", fontWeight: 900 }}
                            >
                              Este returnat?
                            </Typography>
                            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                              <Button
                                type="button"
                                size="small"
                                variant="contained"
                                color="error"
                                aria-label={`Confirmă returul pentru ${product.sku}`}
                                disabled={Boolean(refundReviewSaving)}
                                onClick={() => resolveRefundReview(product, true)}
                                sx={{
                                  minWidth: 48,
                                  px: 1,
                                  textTransform: "none",
                                  fontWeight: 900,
                                }}
                              >
                                {refundReviewSavingForProduct &&
                                refundReviewSaving?.returned
                                  ? "..."
                                  : "DA"}
                              </Button>
                              <Button
                                type="button"
                                size="small"
                                variant="outlined"
                                aria-label={`Păstrează vândut produsul ${product.sku}`}
                                disabled={Boolean(refundReviewSaving)}
                                onClick={() => resolveRefundReview(product, false)}
                                sx={{
                                  minWidth: 48,
                                  px: 1,
                                  color: "#475467",
                                  borderColor: "#98a2b3",
                                  background: "#fff",
                                  textTransform: "none",
                                  fontWeight: 900,
                                }}
                              >
                                {refundReviewSavingForProduct &&
                                refundReviewSaving?.returned === false
                                  ? "..."
                                  : "NU"}
                              </Button>
                            </Stack>
                          </Box>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {purchasePriceUnavailable
                        ? "-"
                        : product.status === "sold"
                        ? formatMoney(product.realizedProfit)
                        : formatMoney(product.unrealizedProfit)}
                      <Typography variant="caption" display="block" sx={{ color: "#667085" }}>
                        curs {numberFormatter.format(product.purchaseFxRateToRon || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {purchasePriceUnavailable
                        ? "-"
                        : `${numberFormatter.format(product.roi || 0)}%`}
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.75}
                        justifyContent="flex-end"
                        sx={{ minWidth: 92 }}
                      >
                        <Tooltip title="Editează">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Editează produs finanțat"
                              onClick={() => startProductEdit(product)}
                              disabled={productEditSaving || Boolean(productDeleteId)}
                              sx={{
                                width: 34,
                                height: 34,
                                border: "1px solid #cbd5e1",
                                borderRadius: "8px",
                                color: "#2563eb",
                                background: "#fff",
                                "&:hover": {
                                  background: "#eff6ff",
                                  borderColor: "#93c5fd",
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            Number(productDeleteId) === Number(product.id)
                              ? "Se șterge"
                              : "Șterge"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Șterge produs finanțat"
                              onClick={() => deleteProduct(product)}
                              disabled={
                                productEditSaving || Boolean(productDeleteId)
                              }
                              sx={{
                                width: 34,
                                height: 34,
                                border: "1px solid #fecaca",
                                borderRadius: "8px",
                                color: "#dc2626",
                                background: "#fff",
                                "&:hover": {
                                  background: "#fef2f2",
                                  borderColor: "#fca5a5",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    </TableRow>
                  );
                })}
                {!data.products.length && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      Nu există produse finanțate încă.
                    </TableCell>
                  </TableRow>
                )}
                {data.products.length > 0 && !filteredProducts.length && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      Nu am găsit produse pentru căutarea curentă.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      )}

      {tab === "ledger" && (
        <Section
          title="Registru tranzacții"
          subtitle="Sursa de adevăr pentru capital, achiziții, vânzări, costuri și dobânzi."
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Pool</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">Sumă</TableCell>
                  <TableCell>Descriere</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.transactions.map((transaction) => (
                  <TableRow key={transaction.id} hover>
                    <TableCell>{formatDate(transaction.transactionDate)}</TableCell>
                    <TableCell>{typeLabel(transaction.type)}</TableCell>
                    <TableCell>
                      {transaction.investorName || "-"}
                      <Typography variant="caption" display="block" sx={{ color: "#667085" }}>
                        {transaction.poolName || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>{transaction.sku || "-"}</TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          color: transaction.amount < 0 ? "#b42318" : "#067647",
                          fontWeight: 800,
                        }}
                      >
                        {formatMoney(transaction.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>{transaction.description || "-"}</TableCell>
                  </TableRow>
                ))}
                {!data.transactions.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nu există tranzacții încă.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ color: "#667085" }}>
            Regula de audit: capitalul nu se editează direct; se schimbă doar prin
            tranzacții `capital_in`, `capital_out`, `product_purchase`,
            `product_sale`, `interest_due`, `interest_paid` și `expense`. Cash-ul
            disponibil exclude tranzacțiile `expense` și folosește costul alocat la
            vânzare, nu valoarea încasată.
          </Typography>
        </Section>
      )}
      <Snackbar
        open={Boolean(productSyncMessage?.open)}
        autoHideDuration={7500}
        onClose={closeProductSyncSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeProductSyncSnackbar}
          severity={productSyncMessage?.type || "info"}
          variant="filled"
          sx={{
            width: "100%",
            minWidth: { xs: "auto", sm: 420 },
            borderRadius: "12px",
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.22)",
            fontWeight: 800,
          }}
        >
          {productSyncMessage?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default InvestitiiPage;
